/**
 * What the tile under me offers, whatever I'm doing: a button each. An extractor is harvested and
 * tells about itself, a charge tells about itself (its fuse), an infuser is loaded with my blocks,
 * loose ground is gathered by hand, the hub opens its card. One list from the facts, one press for
 * all of them.
 */
import {
  BLOCKS,
  KIND,
  roomOf,
  type Block,
  type Cost,
  type Item,
  type MachineType,
} from '@bramvasterd/terrella-rules';

import type { Blueprint } from '../build/blueprints';
import type { Game } from '../game';
import { submitAction, waitingForServer } from '../intents/submit';
import { factsHere } from '../planet/here';
import { spendableOf } from '../planet/pockets';
import type { TileFacts } from '../planet/tile';
import { openHub } from '../view/hub';
import { openPanel } from '../view/panels';

/**
 * Why an option can't happen right now, though it is what the button stands for: waiting for the
 * server, nothing in it, no room for it (my pockets, an infuser's hopper), nothing of mine for it.
 */
export type Blocked = 'waiting' | 'empty' | 'full' | 'none' | null;

export type TileOption =
  /** Collect what the extractor here holds. */
  | {
      kind: 'collect';
      machine: MachineType;
      /** What it holds, whole, by item, and all together. */
      holds: Cost;
      amount: number;
      /** What of that fits in my pockets (rules 21), by item and all together. */
      fits: Cost;
      fit: number;
      blocked: Blocked;
    }
  /** The sheet about what stands here, built from `about`. */
  | { kind: 'info'; about: Blueprint; blocked: null }
  /** Gather the loose ground here by hand (rules 24): its block into my pockets, the tile a level down. */
  | { kind: 'gather'; block: Block; blocked: Blocked }
  /**
   * Load the infuser here with my blocks (rules 27): the kinds I carry, and how many more its
   * hopper takes. One kind goes in at once; with several I pick one.
   */
  | { kind: 'load'; blocks: readonly Block[]; room: number; blocked: Blocked }
  /** The hub's card: invite, join, create. */
  | { kind: 'hub'; blocked: null };

/** How much more of an item my pockets take (the rules' `roomOf`: without end in admin mode). */
export type RoomFor = (item: Item) => number;

/**
 * The options of what stands on a tile; while the server has the last word, the actions wait.
 * With the room in my pockets given, Harvest knows what of a machine's hold fits and Gather when
 * my pockets are full (all of it fits without).
 */
export function tileOptions(
  facts: TileFacts,
  waiting: boolean,
  roomFor: RoomFor | null = null,
  blocks: readonly Block[] = [],
): TileOption[] {
  const wait = (blocked: Blocked): Blocked => (waiting && !blocked ? 'waiting' : blocked);
  const { machine } = facts;
  if (facts.kind === KIND.HUB) return [{ kind: 'hub', blocked: null }];
  if (!machine) {
    if (!facts.gathers) return [];
    const full = roomFor !== null && roomFor(facts.gathers) === 0;
    return [{ kind: 'gather', block: facts.gathers, blocked: wait(full ? 'full' : null) }];
  }
  const info: TileOption = {
    kind: 'info',
    about: { kind: 'machine', type: machine.type },
    blocked: null,
  };
  switch (machine.role) {
    case 'charge':
      // a charge only burns its fuse: its sheet says when it goes off and what it breaks
      return [info];
    case 'infuser': {
      const room = machine.capacity - machine.hopper.length;
      const blocked = blocks.length === 0 ? 'none' : room <= 0 ? 'full' : null;
      return [{ kind: 'load', blocks, room, blocked: wait(blocked) }, info];
    }
    case 'extractor': {
      const held: Cost = { [machine.resource]: machine.stored };
      const holds: Cost = {};
      const fits: Cost = {};
      let amount = 0;
      let fit = 0;
      for (const [item, n] of Object.entries(held) as [Item, number][]) {
        if (n <= 0) continue;
        holds[item] = n;
        amount += n;
        const fitting = roomFor ? Math.min(n, roomFor(item)) : n;
        if (fitting > 0) fits[item] = fitting;
        fit += fitting;
      }
      return [
        {
          kind: 'collect',
          machine: machine.type,
          holds,
          amount,
          fits,
          fit,
          blocked: wait(amount === 0 ? 'empty' : fit === 0 ? 'full' : null),
        },
        info,
      ];
    }
  }
}

/** The options on the tile under me, or none while I'm not on the planet. */
export function currentOptions(game: Game): TileOption[] {
  const facts = factsHere(game);
  const s = game.store.get();
  const { view } = s;
  const me = s.me?.id;
  const roomFor: RoomFor | null =
    view && me !== undefined ? (item) => roomOf(view, me, item) : null;
  // the kinds of block I carry (every kind in admin mode): what an infuser takes
  const mine = spendableOf(view, me ?? null);
  const blocks = BLOCKS.filter((block) => mine[block] > 0);
  return facts ? tileOptions(facts, waitingForServer(s), roomFor, blocks) : [];
}

/** Press an option on the tile under me; false when it can't happen now. */
export function pressOption(game: Game, option: TileOption): boolean {
  const s = game.store.get();
  if (!s.view || s.here < 0 || option.blocked) return false;
  const tile = s.here;
  switch (option.kind) {
    case 'collect':
      return submitAction(game, { kind: 'collect', tile });
    case 'gather':
      return submitAction(game, { kind: 'gather', tile });
    case 'load': {
      // one kind goes in at once; with several, a sheet to pick one
      const [only] = option.blocks;
      if (option.blocks.length === 1 && only) return loadInfuser(game, only);
      openPanel(game, 'load', tile);
      return true;
    }
    case 'info':
      openPanel(game, 'info', tile);
      return true;
    case 'hub':
      openHub(game);
      return true;
  }
}

/** Load the infuser under me with every `block` I carry that its hopper takes. */
export function loadInfuser(game: Game, block: Block): boolean {
  const tile = game.store.get().here;
  return tile >= 0 && submitAction(game, { kind: 'load', tile, item: block });
}
